import { Injectable } from '@angular/core';
import { SPProductFilterOptions,QueryFilter,SpProductOrLocationExportPayload,SPViewFilterOptions } from '../types/solution-participation-types';

type FilterOptions = SPProductFilterOptions | SPViewFilterOptions;

@Injectable({
  providedIn: 'root'
})
export class SpExcelExportService {

  // Define column name mappings if your database columns differ from property names
  columnMappings: Record<string, string> = {
    accountNumber: 'ACCOUNTNUMBER',
    accountName: 'ACCOUNTNAME',
    country: 'COUNTRY',
    state: 'STATE',
    city: 'CITY',
    zipCode: 'ZIPCODE',
    productSku: 'PRODUCTCODE',
    brand: 'BRAND',
    productGroup: 'PRODUCTGROUP',
    productName: 'PRODUCTNAME',
  };

  constructor() { }



  /// This method returns the column name based on the sortColumn parameter
  /// It is used to map the sort column to the actual data field name in BE Database.
  getSortedColumnNameForLocation(sortColumn:string): string {
    switch (sortColumn) {
      case 'location':
        //This is used to sort by MDM_SITE_ID in the backend database. In the UI, MDM_SITE_ID is treated
        //as a number, but in the DB, it is stored as a string. Therefore, we cast it to a number for 
        //sorting purposes, ensuring that existing functionalities remain unchanged.
        return 'CAST(MDM_SITE_ID AS number)';
      case 'sales':
        return 'TotalSales';
      case 'cases':
        return 'TotalCases';
      default:
        return 'CAST(MDM_SITE_ID AS number)';
    }
  }

  executeBrowserDownload(fileData: BlobPart, fileName: string) {
    const url = window.URL.createObjectURL(new Blob([fileData]));
    const link = document.createElement('a');
    link.href = encodeURI(url);
    link.setAttribute('download', fileName);
    document.body.appendChild(link);
    link.click();
  }

    /**
   * Updates or adds a filter to the provided payload's queryFilters array.
   * This is a generic method and can be used for any payload with a queryFilters property.
   * @param payload The object containing the queryFilters array.
   * @param columnName The column name to filter on.
   * @param filterValue The value to filter by.
   * @param operation The filter operation ("And" | "Or").
   * @param logicalOperator The logical operator ("In" | "Not In" | "Equals" | "ILike" | "=" | "!=").
   */
  upsertQueryFilter(
    payload: SpProductOrLocationExportPayload,
    columnName: string,
    filterValue: any,
    filterOperation: "In" | "Not In" | "Equals" | "ILike" | "=" | "!=",
    logicalOperator:  "And" | "Or",
  ): void {
    if (!this.isValidFilterValue(filterValue)) return;

    payload.queryFilters = payload.queryFilters || [];
    const existingFilter = payload.queryFilters.find(
      filter => columnName in filter.columnFilters
    );

    if (existingFilter) {
      const existingValue = existingFilter.columnFilters[columnName];
      if (this.areValuesEqual(existingValue, filterValue)) 
        return;

      existingFilter.columnFilters[columnName] = filterValue;
      existingFilter.filterOperation = filterOperation;
      existingFilter.logicalOperator = logicalOperator;
    } else {
      const queryFilter: QueryFilter = {
        columnFilters: { [columnName]: filterValue },
        filterOperation,
        logicalOperator
      };
      payload.queryFilters.push(queryFilter);
    }
  }

  /**
   * Checks if the filter value is valid (non-empty array or non-empty string).
   */
  isValidFilterValue(filterValue: any): boolean {
    return (
      (Array.isArray(filterValue) && filterValue.length > 0) ||
      (typeof filterValue === 'string' && filterValue.length > 0)
    );
  }

  /**
   * Compares two filter values for equality.
   */
  areValuesEqual(existingValue: any, newValue: any): boolean {
    if (Array.isArray(existingValue) && Array.isArray(newValue)) {
      return (
        existingValue.length === newValue.length &&
        existingValue.every(item => newValue.includes(item))
      );
    }
    return existingValue === newValue;
  }

    /**
   * Updates the advanced filters based on the selected filters.
   * It removes filters that are now empty/cleared and adds new filters for any missing keys.
   * @param selectedFilters The selected filters to update.
   * @returns The updated query filters.
   */
  upsertAdvanceFilters(selectedFilters: FilterOptions,payload:SpProductOrLocationExportPayload): QueryFilter[] {
        // Remove filters that are now empty/cleared
    const activeFilterKeys = Object.entries(selectedFilters)
      .filter(([, values]) => values?.length)
      .map(([key]) => key);

    // Map the keys to their corresponding column names
    const activeMappedColumns = activeFilterKeys.map(key => 
      this.columnMappings[key]
    );

    // Keep only filters that still have values (using mapped column names)
    const updatedFilters = payload.queryFilters?.filter(filter =>
      activeMappedColumns.some(mappedColumn => mappedColumn in filter.columnFilters)
    );

    // Add new filters for any missing keys (using mapped column names)
    Object.entries(selectedFilters).forEach(([key, values]) => {
      if (values?.length) {
        const mappedColumn = this.columnMappings[key];
        
        // Check if filter already exists using the mapped column name
        if (!updatedFilters?.some(f => mappedColumn in f.columnFilters)) {
          updatedFilters?.push({
            columnFilters: { [mappedColumn]: values },
            filterOperation: values.length === 1 ? "=" : "In",
            logicalOperator: "And"
          });
        }
      }
    });

    // If no filters are left, set queryFilters to an empty array
    if (updatedFilters?.length === 0) {
        payload.queryFilters = [];
    }

    return updatedFilters ?? [];
  }

}
